import { EllipsisOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons"
import type { DragEndEvent } from "@dnd-kit/core/dist/types/events"
import { COLOR } from "@finway-group/shared/lib/consts"
import { RightEnum, TransactionCategory, TransactionCategoryTypeEnum } from "@finway-group/shared/lib/models"
import { Button, Col, Dropdown, Menu, Table, Tabs } from "antd"
import { ColumnsType } from "antd/lib/table"
import React, { useCallback, useEffect } from "react"
import { Edit as EditIcon, Trash as TrashIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { CornerDownRightArrowIcon } from "~Components/icons/CornerDownRightArrowIcon"
import { ExpandableListItemIcon } from "~Components/icons/ExpandableListItemIcon"
import { MenuTwoLinesIcon } from "~Components/icons/MenuTwoLinesIcon"
import { ErrorTable } from "~Components/layout/errorTable"
import TransactionCategoryModal, { ModalTypeEnum } from "~Components/modals/transactionCategory.modal"
import { PageBody } from "~Components/page/pageBody"
import SearchInput from "~Components/searchInput"
import { DraggableRow } from "~Components/tables/draggableRow"
import DraggableTableWrapper from "~Components/tables/draggableTableWrapper"
import UpgradeAlert from "~Components/upgradeAlert"
import TransactionCategoryListBanner from "~Features/pages/liquidity/tabs/transactionCategoryListBanner"
import { useModal } from "~Shared/context/modal.context"
import { useIsFreeVersion } from "~Shared/hooks/featureFlags.hooks"
import { useLiquidityActiveTab } from "~Shared/hooks/liquidity.hooks"
import { DraggableTableDataSourceType, HandleDragEndParams, useDraggableTable, useFetchTable, useTable } from "~Shared/hooks/table.hooks"
import { OTHER_INFLOWS_EXTERNAL_NAME, OTHER_OUTFLOWS_EXTERNAL_NAME } from "~Shared/hooks/transactionCategory.hooks"
import { AuthzService } from "~Shared/services"
import { refetchTable, updateTableOnChange, updateTableSearch } from "~Shared/store/actions/tables/tableActions"
import { updateTransactionCategoryOrder } from "~Shared/store/actions/transactionCategory/transactionCategoryAction"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { Table as TableInterface, TransactionCategoryWithChildren } from "~Shared/store/reducers/tableReducer"
import useStateIfMounted from "~Shared/utils/hooks/useStateIfMounted"
import { getSortOrderForColumn } from "~Shared/utils/table.utils"
import { LMTabTypeEnum } from "../liquidity.types"

interface TransactionCategoriesTabProps {
    setControls: (controls: JSX.Element) => void
}

const TransactionCategoriesTab = ({ setControls }: TransactionCategoriesTabProps) => {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useStateIfMounted<TransactionCategoryTypeEnum>(TransactionCategoryTypeEnum.INFLOW)
    const dispatch = useDispatch()
    const { showModal } = useModal()
    const isFreeVersion = useIsFreeVersion()
    const liquidityActiveTab = useLiquidityActiveTab()

    const transactionCategoriesInflowTable = useTable<TransactionCategoryWithChildren>(TablesEnum.TRANSACTION_CATEGORIES_INFLOW)
    const transactionCategoriesOutflowTable = useTable<TransactionCategoryWithChildren>(TablesEnum.TRANSACTION_CATEGORIES_OUTFLOW)

    useFetchTable(TablesEnum.TRANSACTION_CATEGORIES_INFLOW)
    useFetchTable(TablesEnum.TRANSACTION_CATEGORIES_OUTFLOW)

    const handleTabClick = (key: TransactionCategoryTypeEnum) => {
        if (key === activeTab) {
            return
        }
        setActiveTab(key)
        if (!isFreeVersion) {
            setControls(renderControls(key))
        }
    }

    useEffect(() => {
        if (!isFreeVersion && liquidityActiveTab === LMTabTypeEnum.TRANSACTION_CATEGORIES) {
            setControls(renderControls(activeTab))
        }
    }, [liquidityActiveTab])

    const handleSelect = (option: ModalTypeEnum, transactionCategory?: TransactionCategoryWithChildren, transactionCategoryType?: TransactionCategoryTypeEnum) => {
        switch (option) {
            case ModalTypeEnum.CREATE:
                showModal(TransactionCategoryModal, true, { isShowing: true, modalType: ModalTypeEnum.CREATE, transactionCategoryType })
                break
            case ModalTypeEnum.EDIT:
                showModal(TransactionCategoryModal, true, { isShowing: true, modalType: ModalTypeEnum.EDIT, transactionCategory })
                break
            case ModalTypeEnum.DELETE:
                showModal(TransactionCategoryModal, true, { isShowing: true, modalType: ModalTypeEnum.DELETE, transactionCategory })
                break
            default:
                break
        }
    }

    const getTableColumns = (table: TableInterface<TransactionCategoryWithChildren>) => {
        const columns: ColumnsType<TransactionCategoryWithChildren> = [
            {
                colSpan: 0,
                key: "order",
                width: 35,
                ellipsis: true,
                className: "drag-visible",
                align: "center",
                render: (transactionCategory: TransactionCategory) => (
                    <div key={transactionCategory.id} className="flex mb-4">
                        {/* if table is sorted or filtered we dont show the drag handle */}
                        {!transactionCategory.parentId && !table.data.sort.order && (!table.data.search || table.data.search.length === 0) ? (
                            <MenuTwoLinesIcon style={{ cursor: "grab", color: COLOR.gray[900] }} />
                        ) : (
                            <></>
                        )}
                    </div>
                ),
            },
            {
                colSpan: 0,
                dataIndex: "expandIcon",
                width: 46,
                align: "center",
            },
            {
                colSpan: 3,
                key: "name",
                title: t("label:categories"),
                align: "left",
                className: "transaction-category-name",
                sorter: (a, b) => `${a.name} ${a.name}`.localeCompare(`${b.name} ${b.name}`),
                sortOrder: getSortOrderForColumn(table, "name"),
                render: (transactionCategory: TransactionCategoryWithChildren) => (
                    <div className="flex justify-between items-center w-full">
                        <div key={transactionCategory.id}>{transactionCategory.name}</div>
                        <Dropdown
                            className="flex items-center z-100"
                            overlay={
                                <Menu onClick={(props) => handleSelect(props.key as ModalTypeEnum, transactionCategory)}>
                                    <Menu.Item key={ModalTypeEnum.EDIT} className="flex items-center py-8" icon={<EditIcon size={14} />}>
                                        <p className="leading-none text-text">{t("input:transaction_category.edit")}</p>
                                    </Menu.Item>
                                    {transactionCategory.externalName !== OTHER_INFLOWS_EXTERNAL_NAME && transactionCategory.externalName !== OTHER_OUTFLOWS_EXTERNAL_NAME && (
                                        <Menu.Item key={ModalTypeEnum.DELETE} className="flex items-center py-8 " icon={<TrashIcon size={14} />}>
                                            <p className="leading-none text-text">{t("input:transaction_category.delete")}</p>
                                        </Menu.Item>
                                    )}
                                </Menu>
                            }
                            trigger={["click"]}
                        >
                            <Button className="w-34" icon={<EllipsisOutlined size={34} />}></Button>
                        </Dropdown>
                    </div>
                ),
            },
        ]
        return columns
    }

    const onExpandIcon = (props: any) => {
        // if the category does not have a parent
        if (!props.record.parentId) {
            // if it does not have any children render nothing
            if (props.record.children?.length === 0) {
                return <></>
            }

            // if it has children render the collapse up/down icon
            return (
                <div
                    role="button"
                    onClick={(e: any) => {
                        props.onExpand(props.record, e)
                    }}
                >
                    <ExpandableListItemIcon expanded={props.expanded} />
                </div>
            )
        }
        // if the category has a parent
        return <CornerDownRightArrowIcon />
    }

    const searchCallback = useCallback(
        (value: string) => {
            dispatch(updateTableSearch(TablesEnum.TRANSACTION_CATEGORIES_INFLOW, value))
            dispatch(updateTableSearch(TablesEnum.TRANSACTION_CATEGORIES_OUTFLOW, value))
        },
        [dispatch],
    )

    const handleDragEnd = async ({ overIndex, newList }: HandleDragEndParams<TransactionCategoryWithChildren>) => {
        const transactionCategoryId = newList[overIndex]?.id
        // It needs the ID of the one being moved
        // And the order of the one it replaces
        await updateTransactionCategoryOrder(transactionCategoryId, overIndex)(dispatch)
    }

    const {
        dataSource: dataSourceInflow,
        onDragEnd: onDragEndInflow,
        isLoading: isLoadingInflow,
        onDragStart: onDragStartInflow,
        activeId: activeIdInflow,
    } = useDraggableTable({
        dataSource: transactionCategoriesInflowTable.data.docs,
        handleDragEnd,
    })

    const {
        dataSource: dataSourceOutflow,
        onDragEnd: onDragEndOutflow,
        onDragStart: onDragStartOutflow,
        isLoading: isLoadingOutflow,
        activeId: activeIdOutflow,
    } = useDraggableTable({
        dataSource: transactionCategoriesOutflowTable.data.docs,
        handleDragEnd,
    })

    const renderTabPane = ({
        type,
        tableEnum,
        table,
        dataSource,
        onDragEnd,
        onDragStart,
        activeId,
    }: {
        type: TransactionCategoryTypeEnum
        tableEnum: TablesEnum
        table: TableInterface<TransactionCategoryWithChildren>
        dataSource: Array<DraggableTableDataSourceType<TransactionCategoryWithChildren>>
        onDragEnd: (event: DragEndEvent) => void
        onDragStart: (event: DragEndEvent) => void
        activeId: string | null
    }) => (
        <Tabs.TabPane forceRender={true} tab={`${t(`label:${type.toLocaleLowerCase()}`)}`} key={type}>
            <PageBody>
                {table.error ? (
                    <ErrorTable onTableReload={() => dispatch(refetchTable(tableEnum))} isLoading={table.isFetching} />
                ) : (
                    <DraggableTableWrapper<TransactionCategoryWithChildren>
                        dataSource={dataSource}
                        onDragEnd={onDragEnd}
                        columns={getTableColumns(table)}
                        onDragStart={onDragStart}
                        activeId={activeId}
                    >
                        <Table
                            columns={getTableColumns(table)}
                            dataSource={dataSource}
                            components={{
                                body: {
                                    row: DraggableRow,
                                },
                            }}
                            loading={{
                                spinning: table.isFetching || isLoadingInflow || isLoadingOutflow,
                                indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                            }}
                            onChange={updateTableOnChange(tableEnum, table.data, false)}
                            expandIconColumnIndex={1}
                            pagination={{
                                position: ["bottomRight"],
                                showSizeChanger: true,
                                defaultPageSize: 20,

                                hideOnSinglePage: false,
                                pageSizeOptions: ["5", "10", "20", "50", "100"],
                            }}
                            expandIcon={(props) => props.record.children && onExpandIcon(props)}
                        />
                    </DraggableTableWrapper>
                )}
            </PageBody>
        </Tabs.TabPane>
    )

    const renderControls = (categoryType: TransactionCategoryTypeEnum) => (
        <>
            <SearchInput onSearch={searchCallback} value={transactionCategoriesInflowTable.data.search} />
            {AuthzService.isRightGrantedForLoggedInUser(RightEnum.LIQUIDITY__ALL__UPDATE) && (
                <Button type="primary" onClick={() => handleSelect(ModalTypeEnum.CREATE, undefined, categoryType)} icon={<PlusOutlined />}>
                    <span>{t(`action:transaction_category.new_${categoryType.toLocaleLowerCase()}`)}</span>
                </Button>
            )}
        </>
    )

    return (
        <>
            {isFreeVersion && (
                <>
                    <div className="flex gap-10 justify-between items-center mb-16">
                        <h1>{t("label:liquidity.tabs.transaction_categories")}</h1>
                        <div className="flex flex-row space-x-10">
                            <div className="flex btn-wrapper">{renderControls(activeTab)}</div>
                        </div>
                    </div>

                    <TransactionCategoryListBanner />
                </>
            )}
            <Tabs id="inflow-outflow-tabs" defaultActiveKey={TransactionCategoryTypeEnum.INFLOW} activeKey={activeTab} onTabClick={handleTabClick} className="pb-10">
                {renderTabPane({
                    type: TransactionCategoryTypeEnum.INFLOW,
                    tableEnum: TablesEnum.TRANSACTION_CATEGORIES_INFLOW,
                    table: transactionCategoriesInflowTable,
                    dataSource: dataSourceInflow,
                    onDragEnd: onDragEndInflow,
                    onDragStart: onDragStartInflow,
                    activeId: activeIdInflow,
                })}
                {renderTabPane({
                    type: TransactionCategoryTypeEnum.OUTFLOW,
                    tableEnum: TablesEnum.TRANSACTION_CATEGORIES_OUTFLOW,
                    table: transactionCategoriesOutflowTable,
                    dataSource: dataSourceOutflow,
                    onDragEnd: onDragEndOutflow,
                    onDragStart: onDragStartOutflow,
                    activeId: activeIdOutflow,
                })}
            </Tabs>
            {isFreeVersion && (
                <Col span={24}>
                    <UpgradeAlert />
                </Col>
            )}
        </>
    )
}

export default TransactionCategoriesTab
